<template>
  <div 
    v-if="soilAnalysisContext && soilAnalysisContext.soilTypes" 
    class="soil-property-selector-widget"
    :class="{ 'disabled': disabled }"
  >
    <div class="soil-property-selector-content">
      <div class="soil-property-header">
        <span class="soil-property-title">
          {{ disabled ? 'Soil Nutrients (Disabled)' : 'Soil Property' }}
        </span>
        <span v-if="disabled" class="disabled-note">(Enable in Map Layers)</span>
      </div>
      

      <div class="soil-property-controls">
        <!-- Left Arrow -->
        <button 
          @click="previousSoilType"
          :disabled="isLoading || currentSoilIndex === 0 || disabled"
          class="arrow-button"
          title="Previous soil property"
        >
          <svg class="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <!-- Original Dropdown -->
        <select
          v-model="selectedSoilType"
          @change="onSoilTypeChange"
          class="soil-select"
          :disabled="isLoading || disabled"
        >
          <option
            v-for="(singleType, index) in soilAnalysisContext.soilTypes"
            :key="index"
            :value="singleType.value"
          >
            {{ singleType.name }}
          </option>
        </select>
        
        <!-- Right Arrow -->
        <button 
          @click="nextSoilType"
          :disabled="isLoading || currentSoilIndex === soilAnalysisContext.soilTypes.length - 1 || disabled"
          class="arrow-button"
          title="Next soil property"
        >
          <svg class="arrow-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-indicator">
        <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import { soilAnalysisStore } from '../../../stores/soilAnalysisStore.js';

export default {
  name: 'SoilPropertySelector',
  props: {
    soilAnalysisContext: {
      type: Object,
      default: null
      // Expected structure: { task: String|Number, farm: String|Number, soilTypes: Array }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      selectedSoilType: 'ph_water',
      isLoading: false,
      previousContext: null
    }
  },
  
  computed: {
    currentSoilIndex() {
      if (!this.soilAnalysisContext?.soilTypes) return 0;
      return this.soilAnalysisContext.soilTypes.findIndex(type => type.value === this.selectedSoilType);
    }
  },
  
  mounted() {
    // Add keyboard event listeners
    document.addEventListener('keydown', this.handleKeydown);
  },
  
  beforeUnmount() {
    // Remove keyboard event listeners
    document.removeEventListener('keydown', this.handleKeydown);
  },
  
  watch: {
    soilAnalysisContext: {
      handler(newContext) {
        // Only reset to default when the actual context identifiers (task/farm) change
        if (newContext && newContext.soilTypes && newContext.soilTypes.length > 0) {
          const contextKey = `${newContext.task}-${newContext.farm}`;
          const previousContextKey = this.previousContext ? 
            `${this.previousContext.task}-${this.previousContext.farm}` : null;
          
          // Only reset selection if this is a truly different context (different task/farm)
          if (!this.previousContext || contextKey !== previousContextKey) {
            // Find ph_water as default, or use first available
            const defaultType = newContext.soilTypes.find(type => type.value === 'ph_water') 
              || newContext.soilTypes[0];
            this.selectedSoilType = defaultType.value;
            this.loadSoilData();
          }
          
          // Always update the previous context reference
          this.previousContext = { ...newContext };
        }
      },
      immediate: true
    }
  },
  
  methods: {
    async onSoilTypeChange() {
      
      await this.loadSoilData();
    },
    
    previousSoilType() {
      if (!this.soilAnalysisContext?.soilTypes || this.isLoading) return;
      
      const currentIndex = this.currentSoilIndex;
      if (currentIndex > 0) {
        const newIndex = currentIndex - 1;
        this.selectedSoilType = this.soilAnalysisContext.soilTypes[newIndex].value;
        this.onSoilTypeChange();
      }
    },
    
    nextSoilType() {
      if (!this.soilAnalysisContext?.soilTypes || this.isLoading) return;
      
      const currentIndex = this.currentSoilIndex;
      if (currentIndex < this.soilAnalysisContext.soilTypes.length - 1) {
        const newIndex = currentIndex + 1;
        this.selectedSoilType = this.soilAnalysisContext.soilTypes[newIndex].value;
        this.onSoilTypeChange();
      }
    },
    
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    
    selectSoilType(soilTypeValue) {
      this.selectedSoilType = soilTypeValue;
      this.showDropdown = false;
      this.onSoilTypeChange();
    },
    
    handleKeydown(event) {
      // Only handle arrows when widget is visible and not loading
      if (!this.soilAnalysisContext?.soilTypes || this.isLoading) return;
      
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previousSoilType();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.nextSoilType();
      }
    },
    
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.showDropdown = false;
      }
    },
    
    async loadSoilData() {
      if (!this.soilAnalysisContext || this.isLoading || this.disabled) return;
      
      this.isLoading = true;
      
      try {
        
        
        const response = await axios.get(
          route("app.soil-analysis.chart", {
            soil_type: this.selectedSoilType,
            farm: this.soilAnalysisContext.farm,
            show_by: 'sample_id', // Default to sample_id for map display
            task: this.soilAnalysisContext.task,
          })
        );
        
        
        
        // Update global state that the map watches (this will trigger layer clearing and re-rendering)
        
        this.$root.sampleZones = response.data.mapData;
        
        // Also update the global soil analysis store to keep everything in sync 
        soilAnalysisStore.updateSelectedSoilType(this.selectedSoilType);
        
        // Emit event for parent components that might need to react
        this.$emit('soil-data-updated', {
          mapData: response.data.mapData,
          statistics: response.data.statistics,
          chartLegend: response.data.chartLegend,
          selectedSoilType: this.selectedSoilType
        });
        
        
      } catch (error) {
        console.error('SoilPropertySelector: Failed to load soil data:', error);
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style scoped>
/* Placed by FarmMap, in the map's bottom-left corner under the soil legend */
.soil-property-selector-widget {
  pointer-events: auto;
}

.soil-property-selector-content {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  min-height: 90px;
  max-height: 150px;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  overflow-y: auto;
}

.soil-property-selector-content:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* Disabled state styling */
.soil-property-selector-widget.disabled .soil-property-selector-content {
  background: rgba(245, 245, 245, 0.9);
  color: #9ca3af;
  cursor: not-allowed;
}

.soil-property-selector-widget.disabled .soil-property-selector-content:hover {
  background: rgba(245, 245, 245, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.soil-property-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
}

.soil-property-title {
  font-size: 14px;
  flex: 1;
}

.disabled-note {
  font-size: 10px;
  color: #6b7280;
  font-weight: 400;
  margin-left: 4px;
}

.soil-property-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.arrow-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #374151;
  flex-shrink: 0;
}

.arrow-button:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #111827;
}

.arrow-button:disabled {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #d1d5db;
  cursor: not-allowed;
}

.arrow-icon {
  width: 16px;
  height: 16px;
}

.soil-property-dropdown {
  width: 100%;
  position: relative;
}

.loading-indicator {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6366f1;
}

.soil-select {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 13px;
  border-radius: 6px;
  padding: 6px 8px;
  flex: 1;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.soil-select:disabled {
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

.soil-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.soil-select:hover {
  border-color: #9ca3af;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .soil-property-selector-widget {
    min-width: 180px;
  }
  
  .soil-property-selector-content {
    min-width: 180px;
    padding: 10px;
  }
  
  .soil-property-title {
    font-size: 13px;
  }
}
</style>